import { useMutation, useQueryClient } from "@tanstack/react-query";
import { TASK_DOMAIN, TASK_PRESET_DETAIL_ENDPOINT, taskApi } from "api";
import { ProgramContentType } from "data/BlockComponent";
import { useSetRecoilState } from "recoil";
import { taskListAtom } from "recoil/taskListAtom";
import useRefetchTaskList from "hooks/useRefetchTaskList";
import React, { useEffect, useState } from "react";
import useGetBlankTaskData from "hooks/useGetBlankTaskData";
import { ProgramType } from "../../../data/programData";
import { Button } from "@mui/joy";
import { ResetIconSvg } from "../../../static/image/common/SvgAssets";
import { theme } from "../../../styles/theme";
import CustomModal from "../../common/CustomModal";

export default function TaskResetButton({
  taskKey,
  setData,
  contentData,
  setContentData,
  thoughtRecordKey,
  activityRecordKey,
  meditationRecordKey,
  isDone = false,
  isInitialized,
  onClose,
}: {
  taskKey: string;
  setData?: React.Dispatch<React.SetStateAction<ProgramType | undefined>>;
  contentData?: ProgramContentType[];
  setContentData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  thoughtRecordKey?: string;
  activityRecordKey?: string;
  meditationRecordKey?: string;
  isDone?: boolean;
  isInitialized?: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();

  const blankTaskData = useGetBlankTaskData({ taskKey: taskKey });

  const setTaskListState = useSetRecoilState(taskListAtom);

  const refetchTaskList = useRefetchTaskList();

  const [disabled, setDisabled] = useState<boolean>(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setDisabled(
      thoughtRecordKey !== undefined ||
        meditationRecordKey !== undefined ||
        !isDone
    );
  }, [isDone]);

  const { mutate: activityUndone } = useMutation(
    () => taskApi.undone(`${taskKey}`),
    {
      onSuccess: () => {
        refetchTaskList();
        setTaskListState((prev) =>
          prev.map((task) =>
            task.taskKey === taskKey
              ? { ...task, isDone: false, isOpen: true }
              : task
          )
        );
      },
    }
  );

  useEffect(() => {
    if (isInitialized && blankTaskData) {
      if (setData) {
        setData(blankTaskData);
      }
      setContentData(blankTaskData.content);
    }
  }, [isInitialized, blankTaskData]);

  const { mutate: activityReset } = useMutation(
    () => taskApi.reset(`${taskKey}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          TASK_DOMAIN,
          TASK_PRESET_DETAIL_ENDPOINT,
          taskKey,
        ]);
      },
    }
  );

  function handleResetAndUndone() {
    if (setData) {
      setData(undefined);
    }
    setContentData(undefined);
    activityReset();
    handleUndone();
    if (blankTaskData) {
      if (setData) {
        setData(blankTaskData);
      }
      setContentData(blankTaskData.content);
    }
  }

  function handleUndone() {
    activityUndone();
  }

  return (
    <>
      <Button
        size="sm"
        variant="Plain"
        color="neutral"
        disabled={disabled}
        startDecorator={<ResetIconSvg />}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        sx={{
          "--Button-gap": "12px",
          width: "100%",
          height: "44px",
          fontSize: "16px",
          fontWeight: 500,
          pl: "20px",
          pr: "24px",
          justifyContent: "flex-start",
          whiteSpace: "nowrap",
          color: theme.vars.palette.Content.Strong,
          backgroundColor: theme.vars.palette.Surface.Forward,
        }}
      >
        {"초기화해서 다시 주기"}
      </Button>
      <CustomModal
        open={open}
        onClose={() => {
          setOpen(false);
          onClose();
        }}
        title={"모든 내용을 지우고, 활동지를 다시 열어줄까요?"}
        subtitle={"이용자가 처음부터 다시 활동지를 진행할 수 있게 돼요."}
        actions={[
          {
            label: "초기화해서 다시 주기",
            onClick: () => {
              handleResetAndUndone();
              setOpen(false);
              onClose();
            },
            variant: "Solid",
            color: "danger",
          },
        ]}
      />
    </>
  );
}